import { useState, useCallback } from "react";

export function useDragReorder(
  onReorder: (fromIndex: number, toIndex: number) => void
) {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [touchStartY, setTouchStartY] = useState<number | null>(null);
  const [ghostPosition, setGhostPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);

  // Desktop drag handlers
  const handleDragStart = useCallback(
    (index: number) => (e: React.DragEvent) => {
      setDraggedIndex(index);
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/html", e.currentTarget.innerHTML);
    },
    []
  );

  const handleDragOver = useCallback(
    (index: number) => (e: React.DragEvent) => {
      if (draggedIndex === null || touchStartY === null) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setDragOverIndex(index);
    },
    []
  );

  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback(
    (index: number) => (e: React.DragEvent) => {
      e.preventDefault();
      if (draggedIndex !== null && draggedIndex !== index) {
        onReorder(draggedIndex, index);
      }
      setDraggedIndex(null);
      setDragOverIndex(null);
    },
    [draggedIndex, onReorder]
  );

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, []);

  // Mobile touch handlers
  const handleTouchStart = useCallback(
    (index: number) => (e: React.TouchEvent) => {
      setDraggedIndex(index);
      setTouchStartY(e.touches[0].clientY);
      setGhostPosition({
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      });
    },
    []
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent | TouchEvent) => {
      // Only handle if we're actually dragging (draggedIndex is set)
      if (draggedIndex === null || touchStartY === null) return;

      const touchX = e.touches[0].clientX;
      const touchY = e.touches[0].clientY;

      // Update ghost position
      setGhostPosition({ x: touchX, y: touchY });

      // Find which card we're hovering over
      const cards = document.querySelectorAll("[data-drag-index]");
      let newOverIndex: number | null = null;

      cards.forEach((card) => {
        const rect = card.getBoundingClientRect();
        if (touchY >= rect.top && touchY <= rect.bottom) {
          const index = parseInt(card.getAttribute("data-drag-index") || "-1");
          if (index !== draggedIndex) {
            newOverIndex = index;
          }
        }
      });

      setDragOverIndex(newOverIndex);
    },
    [draggedIndex, touchStartY]
  );

  const handleTouchEnd = useCallback(
    (_e: React.TouchEvent | TouchEvent) => {
      if (draggedIndex === null) return;

      if (dragOverIndex !== null && draggedIndex !== dragOverIndex) {
        onReorder(draggedIndex, dragOverIndex);
      }

      setDraggedIndex(null);
      setDragOverIndex(null);
      setTouchStartY(null);
      setGhostPosition(null);
    },
    [draggedIndex, dragOverIndex, onReorder]
  );

  return {
    draggedIndex,
    dragOverIndex,
    ghostPosition,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  };
}
